import {
  forwardRef, useEffect, useId, useImperativeHandle, useMemo, useRef, useState,
  type ReactElement,
} from 'react';
import { mascotSvg, type MascotFace } from '../../../../../shared/ember-mascot.js';
import { createMascotMood, type MascotMood } from './mascot-mood';
import '../../../../../shared/ember-mascot.css';

/**
 * The Ember mascot — one self-contained companion any surface can mount
 * (拍板 2026-09-10; the small warm one since 2026-09-13). Drop it in and it
 * lives: listening by default, drifting into a face now and then, answering a
 * tap with another face (mascot-mood.ts). The page tells it what is going on
 * through `base` (`thinking` while a reply is in flight, `curious` while the
 * person writes) and nudges moments through the ref handle (`glad` when a
 * reply lands, `delighted` when the door lights).
 *
 * Pass `face` instead to pin one face with no life of its own — the wait
 * marks (PageLoading, KindleFlame) do this: a spinner must not wander.
 *
 * The drawing is the shared SVG string (src/shared/ember-mascot.js — the same
 * one the server-rendered call page paints). It goes in on mount and never
 * re-renders; the face becomes `data-face` on the wrapper and the shared
 * stylesheet cross-fades faces and swaps the body's loop. Re-rendering on a
 * face change would restart every animation and skip the fade.
 *
 * Sizing: `className` lands on the `.ember-mascot` wrapper (200:240 box) —
 * size it there, or pass `width`. Interactive, it is a button named `label`;
 * pinned or `interactive={false}`, it is decorative and hidden from AT.
 */
export interface EmberMascotHandle {
  nudge: (face: MascotFace) => void;
  poke: () => void;
}

export interface EmberMascotProps {
  /** Pin one face; no mood machine, no drift, no tap. Only that face is drawn. */
  face?: MascotFace;
  /** A pinned face from the past (a turn already read): no breath, no blink. */
  still?: boolean;
  /** The lamp variant (a warmer body, a stronger rim light) — for dark grounds. */
  lit?: boolean;
  /** What is going on, when the mascot lives (ignored with `face`). */
  base?: MascotFace;
  /** Let it drift into a random face while listening. Default true. */
  drift?: boolean;
  /** A tap shows another face. Default true (only while it lives). */
  interactive?: boolean;
  width?: number;
  className?: string;
  /** The button's accessible name when interactive. */
  label?: string;
}

export const EmberMascot = forwardRef<EmberMascotHandle, EmberMascotProps>(function EmberMascot(
  { face, still = false, lit = false, base = 'listening', drift = true, interactive = true, width, className, label = 'Ember' },
  ref,
): ReactElement {
  const pinned = face !== undefined;
  const [lived, setLived] = useState<MascotFace>(base);
  const moodRef = useRef<MascotMood | null>(null);
  useEffect(() => {
    if (pinned) return undefined;
    const mood = createMascotMood({ onFace: setLived, drift });
    moodRef.current = mood;
    return () => { mood.dispose(); moodRef.current = null; };
  }, [pinned, drift]);
  useEffect(() => {
    if (!pinned) moodRef.current?.setBase(base);
  }, [pinned, base]);
  useImperativeHandle(ref, () => ({
    nudge: (f) => { moodRef.current?.nudge(f); },
    poke: () => { moodRef.current?.poke(); },
  }), []);

  const shown = pinned ? face : lived;
  const uid = useId().replace(/[^A-Za-z0-9_-]/g, '');
  const firstFace = useRef(shown);
  // A pinned face never changes, so only it is drawn (a chat thread mounts one
  // per Ember turn); a living mascot carries every face for the cross-fade.
  const stillClass = pinned && still ? 'is-still' : '';
  const wrapperClass = [className, stillClass].filter(Boolean).join(' ');
  const html = useMemo(
    () => mascotSvg({ face: firstFace.current, faces: pinned ? [firstFace.current] : undefined, lit, uid, width, className: wrapperClass }),
    [pinned, lit, uid, width, wrapperClass],
  );
  const host = useRef<HTMLElement>(null);
  useEffect(() => {
    const wrapper = host.current?.querySelector('.ember-mascot');
    if (wrapper) wrapper.setAttribute('data-face', shown);
  }, [shown]);
  // Off the screen it holds still (2026-09-18): a living mascot scrolled out
  // of view (the homepage's contact card under the fold, a long thread's
  // avatars) kept breathing and blinking, and every one of those frames was a
  // full repaint of the page for nobody. The same class a pinned past face
  // wears; it comes off the moment any of it is back in view.
  useEffect(() => {
    const wrapper = host.current?.querySelector('.ember-mascot');
    if (!wrapper || stillClass || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(([entry]) => { wrapper.classList.toggle('is-still', !entry.isIntersecting); });
    io.observe(wrapper);
    return () => io.disconnect();
  }, [html, stillClass]);

  // Our own constant drawing; no user input reaches it.
  if (!pinned && interactive) {
    return (
      <button
        ref={host as React.RefObject<HTMLButtonElement>}
        type="button"
        className="ember-mascot-poke"
        aria-label={label}
        onClick={() => { moodRef.current?.poke(); }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }
  return <span ref={host as React.RefObject<HTMLSpanElement>} style={{ display: 'contents' }} dangerouslySetInnerHTML={{ __html: html }} />;
});
